import React, { useState } from "react";
import { Plus } from "lucide-react";
import { imageUrl } from "../lib/api";
import { useI18n } from "../i18n";
import { ProductModal } from "./ProductModal";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

export function ProductCard({ product, onAdd, disabled }) {
  const { t } = useI18n();
  const [detail, setDetail] = useState(false);
  const inStock = product.in_stock;
  return (
    <div
      data-testid="product-card"
      onClick={() => setDetail(true)}
      className="bg-ink-surface border border-zinc-800 transition-all duration-300 hover:border-neon-green hover:shadow-[0_0_15px_rgba(157,255,0,0.2)] group flex flex-col overflow-hidden cursor-pointer"
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        <img
          src={imageUrl(product.image_url) || PLACEHOLDER}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = PLACEHOLDER; }}
        />
        <span className="absolute top-2 left-2 px-2 py-1 text-[10px] uppercase font-bold text-neon-magenta border border-neon-magenta rounded-full bg-black/60">{product.category}</span>
        {product.promo && (
          <span className="absolute top-2 right-2 px-2 py-1 text-[10px] uppercase font-bold text-black bg-neon-magenta rounded-full glow-magenta" data-testid="promo-badge">{product.promo_pct ? `-${Math.round(product.promo_pct)}%` : t("product.promo")}</span>
        )}
        {inStock && product.quantity > 0 && product.quantity <= 2 && (
          <div data-testid="lowstock-watermark" className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
            <span className="-rotate-12 bg-orange-500/85 text-black font-anton uppercase text-sm sm:text-base tracking-wider px-6 py-1.5 shadow-[0_0_18px_rgba(249,115,22,0.6)] whitespace-nowrap">
              ⚡ {t("product.lastUnits")} {product.quantity} {t("product.inStock")}
            </span>
          </div>
        )}
        {!inStock && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-2">
            {product.restocking ? (
              <span data-testid="restocking-badge" className="text-neon-green font-anton uppercase text-sm border border-neon-green px-3 py-1 text-center">🔄 {t("product.restocking")}</span>
            ) : (
              <span className="text-zinc-300 font-anton uppercase text-sm border border-zinc-500 px-3 py-1">{t("product.out")}</span>
            )}
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-anton text-lg text-white uppercase leading-tight">{product.name}</h3>
        {product.description ? <p className="text-zinc-500 text-xs line-clamp-2">{product.description}</p> : null}
        <div className="flex items-center gap-2 flex-wrap">
          {product.best_seller && <span className="text-[10px] uppercase font-bold text-black bg-neon-green px-1.5 py-0.5" data-testid="bestseller-badge">🔥 {t("product.best")}</span>}
          {product.quantity > 0 && product.quantity <= 3 && <span className="text-[10px] uppercase font-bold text-orange-400 border border-orange-400/50 px-1.5 py-0.5" data-testid="lowstock-badge">⚠️ {t("product.low")} {product.quantity} !</span>}
          <span className="text-[10px] uppercase font-bold text-neon-green border border-neon-green/40 px-1.5 py-0.5" data-testid="thc-badge">🌿 &lt;0,3% THC</span>
          {product.lab_url && (
            <a
              data-testid="lab-report-link"
              href={imageUrl(product.lab_url)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] uppercase font-bold text-neon-magenta border border-neon-magenta/40 px-1.5 py-0.5 hover:bg-neon-magenta hover:text-white transition-colors"
            >
              🔬 {t("product.lab")}
            </a>
          )}
        </div>
        <div className="mt-auto flex flex-col gap-2 pt-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col leading-none min-w-0">
            {product.promo && product.original_price > product.price && (
              <span className="text-xs text-zinc-500 line-through" data-testid="product-original-price">{Number(product.original_price).toFixed(2)}€</span>
            )}
            <span className="font-anton text-2xl text-neon-green" data-testid="product-price">{Number(product.price).toFixed(2)}€</span>
          </div>
          {inStock ? (
            <button
              data-testid="add-to-cart-btn"
              disabled={disabled}
              onClick={(e) => { e.stopPropagation(); if (product.tiers?.length) setDetail(true); else onAdd(product); }}
              className="shrink-0 min-h-[40px] w-full sm:w-auto flex items-center justify-center gap-1 bg-neon-green text-black font-anton uppercase text-xs tracking-wider px-3.5 py-2 hover:bg-neon-greenDark hover:shadow-[0_0_15px_rgba(157,255,0,0.4)] transition-all active:scale-[0.97] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" /> {t("product.add")}
            </button>
          ) : (
            <button data-testid="sold-out-btn" disabled aria-disabled="true" onClick={(e) => e.stopPropagation()}
              className="shrink-0 min-h-[40px] w-full sm:w-auto flex items-center justify-center gap-1 bg-zinc-800 text-zinc-500 font-anton uppercase text-xs tracking-wider px-3.5 py-2 cursor-not-allowed border border-zinc-700">
              {t("product.soldOut")}
            </button>
          )}
        </div>
      </div>
      {detail && <ProductModal product={product} onAdd={onAdd} disabled={disabled} onClose={() => setDetail(false)} />}
    </div>
  );
}
